import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, message, Table, Button, Space, Select } from 'antd';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { EditOutlined, CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { restApi } from '../../../constants/api';
import PersistentDrawerLeft from '../../../components/reusable/admin/Main';

const { Title, Text } = Typography;

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 20px;
  background-color: #f9f9f9;
`;

const StyledCard = styled(Card)`
  max-width: 1000px;
  width: 100%;
  border-radius: 16px;
`;

const StyledButton = styled(Button)`
  background-color: #1677ff;
  border-radius: 8px;
  font-weight: 500;

  &:hover {
    background-color: #0056d2;
  }
`;

const AttendanceCycleDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cycle, setCycle] = useState<any>(null);
  const [attendanceList, setAttendanceList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editedStatus, setEditedStatus] = useState<{ [key: string]: string }>({});

  const statusOptions = ['PRESENT', 'LEAVE', 'ABSENT'];

  // Role Set 
  const rawRole = localStorage.getItem('role');
  const role = rawRole ? rawRole.trim().toUpperCase() : null;
  // console.log("Normalized Role:", role);

  const fetchCycleData = async () => {
    try {
      const res: any = await restApi(`/api/users/cycles/${id}`, 'GET');
      if (Array.isArray(res?.data)) {
        setCycle(res.data[0]);
      }
    } catch (error) {
      message.error('Failed to load cycle data');
    }
  };

  const fetchAttendanceList = async () => {
    try {
      const res: any = await restApi(`/api/attendance?cycle_id=${id}`, 'GET');
      if (Array.isArray(res?.data)) {
        setAttendanceList(res.data);
      }
    } catch (error) {
      message.error('Failed to load attendance list');
    }
  };

  const handleEditClick = (rowId: string, currentStatus: string) => {
    setEditingRow(rowId);
    setEditedStatus({ [rowId]: currentStatus });
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedStatus({});
  };

  const handleStatusChange = (value: string, rowId: string) => {
    setEditedStatus(prev => ({ ...prev, [rowId]: value }));
  };

  const handleStatusUpdate = async (record: any) => {
    try {
      const res: any = await restApi('/api/attendance', 'PATCH', {
        attendance_id: record.id,
        status: editedStatus[record.id],
      });

      if (res.status === 200) {
        message.success('Attendance status updated successfully');
        await fetchAttendanceList();
        setEditingRow(null);
      }
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      Promise.all([fetchCycleData(), fetchAttendanceList()])
        .finally(() => setLoading(false));
    }
  }, [id]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'attendance_date',
      key: 'attendance_date',
      render: (text: string) => dayjs(text).format('DD-MM-YYYY')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => {
        if (editingRow === record.id) {
          return (
            <Select
              value={editedStatus[record.id]}
              onChange={(value) => handleStatusChange(value, record.id)}
              style={{ width: 120 }}
            >
              {statusOptions.map(opt => (
                <Select.Option key={opt} value={opt}>{opt}</Select.Option>
              ))}
            </Select>
          );
        }

        let color = status === 'PRESENT' ? 'green' : status === 'LEAVE' ? 'orange' : 'red';
        return <span style={{ color, fontWeight: 500 }}>{status}</span>;
      }
    },
    ...(role === 'SUPERADMIN'
      ? [
        {
          title: 'Action',
          key: 'action',
          render: (_: any, record: any) => {
            // if (role !== 'SUPERADMIN') return null; // ⛔️ Hide action if not SUPERADMIN
            if (editingRow === record.id) {
              return (
                <Space>
                  <CheckOutlined
                    onClick={() => handleStatusUpdate(record)}
                    style={{ color: 'green', cursor: 'pointer' }}
                  />
                  <CloseOutlined
                    onClick={handleCancelEdit}
                    style={{ color: 'red', cursor: 'pointer' }}
                  />
                </Space>
              );
            }

            return (
              <EditOutlined
                onClick={() => handleEditClick(record.id, record.status)}
                style={{ color: '#1677ff', cursor: 'pointer' }}
              />
            );
          }
        }
      ]
      : [])
  ];

  return (
    <PersistentDrawerLeft>
      <StyledContainer>
        <StyledCard loading={loading}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Title level={4}>Attendance Cycle Details</Title>
            <Button onClick={() => navigate(-1)}>Back</Button>
          </Space>

          {cycle ? (
            <div style={{ marginBottom: 24 }}>
              <Text><strong>User name:</strong> {cycle.user_name}</Text><br />
              <Text><strong>Department:</strong> {cycle.department_name}</Text><br />
              <Text><strong>Designation:</strong> {cycle.user_type_name}</Text><br />
              <Text><strong>Days to Cover:</strong> {cycle.total_number_of_days_need_to_cover}</Text><br />
              <Text><strong>Reminders:</strong> {cycle.no_of_days_reminders} Days</Text><br />
              <Text><strong>Covered:</strong> {cycle.total_number_of_days_covered}</Text><br />
              <Text><strong>Start:</strong> {dayjs(cycle.start_date).format('DD-MM-YYYY')}</Text><br />
              <Text><strong>End:</strong> {dayjs(cycle.end_date).format('DD-MM-YYYY')}</Text>
            </div>
          ) : (
            <Text>Loading cycle details...</Text>
          )}

          <Title level={5} style={{ marginTop: 24 }}>Attendance Records</Title>
          <Table
            dataSource={attendanceList}
            size='small'
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        </StyledCard>
      </StyledContainer>
    </PersistentDrawerLeft>
  );
};

export default AttendanceCycleDetailsPage;
